// Definições de tours de onboarding por perfil de usuário

export interface TourStepDefinition {
  id: string;
  targetSelector: string; // CSS selector like [data-tour="dashboard"]
  title: string;
  description: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

export interface TourDefinition {
  id: string;
  profileType: 'ADMIN' | 'CUSTOMER' | 'COMPLIANCE' | 'OPERATOR';
  title: string;
  steps: TourStepDefinition[];
}

export const tourDefinitions: TourDefinition[] = [
  // Tour para ADMIN
  {
    id: 'admin-onboarding',
    profileType: 'ADMIN',
    title: 'Bem-vindo ao Painel Administrativo',
    steps: [
      {
        id: 'admin-dashboard',
        targetSelector: '[data-tour="dashboard"]',
        title: 'Dashboard',
        description:
          'Aqui você tem uma visão geral do seu tenant: métricas de transações, saldos, usuários ativos e alertas importantes.',
        position: 'bottom',
      },
      {
        id: 'admin-users',
        targetSelector: '[data-tour="users"]',
        title: 'Gestão de Usuários',
        description:
          'Gerencie os usuários do seu tenant. Crie operadores, oficiais de compliance e configure permissões de acesso.',
        position: 'right',
      },
      {
        id: 'admin-modules',
        targetSelector: '[data-tour="modules"]',
        title: 'Configuração de Módulos',
        description:
          'Ative ou desative módulos da plataforma como PIX, Boleto, Tokenização, Crédito e outros conforme o plano do seu tenant.',
        position: 'right',
      },
      {
        id: 'admin-providers',
        targetSelector: '[data-tour="providers"]',
        title: 'Providers',
        description:
          'Configure os provedores de serviço integrados: gateways de pagamento, blockchain, KYC e outros. Cada provider conecta sua plataforma a serviços externos.',
        position: 'right',
      },
      {
        id: 'admin-fees',
        targetSelector: '[data-tour="fees"]',
        title: 'Taxas',
        description:
          'Defina as taxas cobradas nas operações do seu tenant. Configure valores por tipo de transação, faixas de valor e perfis de cliente.',
        position: 'right',
      },
    ],
  },

  // Tour para CUSTOMER
  {
    id: 'customer-onboarding',
    profileType: 'CUSTOMER',
    title: 'Bem-vindo ao Internet Banking',
    steps: [
      {
        id: 'customer-dashboard',
        targetSelector: '[data-tour="dashboard"]',
        title: 'Dashboard',
        description:
          'Sua página inicial com resumo de saldos, últimas transações e atalhos para as operações mais utilizadas.',
        position: 'bottom',
      },
      {
        id: 'customer-statement',
        targetSelector: '[data-tour="statement"]',
        title: 'Extrato',
        description:
          'Consulte o extrato completo da sua conta com filtros por período, tipo de transação e valores. Exporte em PDF ou CSV.',
        position: 'right',
      },
      {
        id: 'customer-pix',
        targetSelector: '[data-tour="pix"]',
        title: 'PIX',
        description:
          'Envie e receba pagamentos instantâneos via PIX. Cadastre chaves, faça transferências e gere QR Codes para cobranças.',
        position: 'right',
      },
      {
        id: 'customer-transfer',
        targetSelector: '[data-tour="transfer"]',
        title: 'Transferências',
        description:
          'Realize transferências entre contas, TED e DOC. Agende transferências futuras ou configure transferências recorrentes.',
        position: 'right',
      },
      {
        id: 'customer-settings',
        targetSelector: '[data-tour="settings"]',
        title: 'Configurações',
        description:
          'Personalize sua conta: altere dados pessoais, configure segurança (2FA, biometria), notificações e preferências de privacidade.',
        position: 'right',
      },
    ],
  },

  // Tour para COMPLIANCE
  {
    id: 'compliance-onboarding',
    profileType: 'COMPLIANCE',
    title: 'Bem-vindo ao Painel de Compliance',
    steps: [
      {
        id: 'compliance-queue',
        targetSelector: '[data-tour="compliance-queue"]',
        title: 'Fila de Aprovação',
        description:
          'Visualize todos os ativos e operações pendentes de aprovação. Priorize por urgência, categoria ou valor e gerencie o fluxo de revisão.',
        position: 'right',
      },
      {
        id: 'compliance-review',
        targetSelector: '[data-tour="compliance-review"]',
        title: 'Revisão de Ativos',
        description:
          'Analise a documentação dos ativos submetidos. Verifique conformidade regulatória, aprove, rejeite ou solicite documentos adicionais.',
        position: 'right',
      },
      {
        id: 'compliance-whitelist',
        targetSelector: '[data-tour="whitelist"]',
        title: 'Whitelist',
        description:
          'Gerencie a lista de investidores autorizados (KYC aprovado). Adicione ou remova endereços e verifique o status de cada investidor.',
        position: 'right',
      },
      {
        id: 'compliance-audit',
        targetSelector: '[data-tour="audit-logs"]',
        title: 'Logs de Auditoria',
        description:
          'Acompanhe todas as ações realizadas na plataforma. Filtre por usuário, módulo, período e tipo de operação para auditoria completa.',
        position: 'right',
      },
    ],
  },

  // Tour para OPERATOR
  {
    id: 'operator-onboarding',
    profileType: 'OPERATOR',
    title: 'Bem-vindo ao Painel de Operações',
    steps: [
      {
        id: 'operator-dashboard',
        targetSelector: '[data-tour="dashboard"]',
        title: 'Dashboard',
        description:
          'Visão operacional do tenant com métricas em tempo real: transações processadas, pendências, alertas e indicadores de performance.',
        position: 'bottom',
      },
      {
        id: 'operator-transactions',
        targetSelector: '[data-tour="transactions"]',
        title: 'Transações',
        description:
          'Monitore e processe transações do tenant. Visualize status, valores e detalhes de cada operação em andamento.',
        position: 'right',
      },
      {
        id: 'operator-approvals',
        targetSelector: '[data-tour="approvals"]',
        title: 'Aprovações',
        description:
          'Gerencie o fluxo de aprovações operacionais. Aprove transferências, operações de tokenização e outras ações que requerem autorização.',
        position: 'right',
      },
      {
        id: 'operator-reports',
        targetSelector: '[data-tour="reports"]',
        title: 'Relatórios',
        description:
          'Acesse relatórios operacionais detalhados: volume de transações, receita, performance de providers e métricas do tenant.',
        position: 'right',
      },
    ],
  },
];
